import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useSetAltPaths } from "../../context/AltPathContext.jsx";

const SITE_NAME = "Tales&Legends";
const SITE_URL = "https://talesandlegends.org";

/**
 * Central SEO component per §10.3 — title/description, hreflang alternates,
 * Open Graph. `noindex` is used for untranslated fallback pages (§10.2).
 *
 * `path` is the CURRENT language's path; `altPath` is the sibling
 * language's path. These are NOT the same string with /de//en/ swapped —
 * most static routes have different slugs per language (e.g. /impressum
 * vs /legal-notice), so the caller must supply both explicitly. For
 * language-identical slugs (events, donation projects), pass the same
 * value for both.
 */
export default function SEOHead({
  lang,
  path,
  altPath = path,
  title,
  category,
  description,
  image,
  noindex = false,
  hasTranslation = true,
}) {
  const canonical = `${SITE_URL}/${lang}${path}`;
  // §10.3 title pattern for knowledge/event pages: "{title} – {category} |
  // Tales&Legends"; everything else keeps the plain "{title} | Tales &
  // Legends" pattern.
  const fullTitle = category ? `${title} – ${category} | ${SITE_NAME}` : `${title} | ${SITE_NAME}`;
  const dePath = lang === "de" ? path : altPath;
  const enPath = lang === "en" ? path : altPath;

  // The header's language switcher needs the exact same DE/EN sibling
  // paths computed above for hreflang — reusing them here means callers
  // don't need to wire this up separately (see AltPathContext.jsx).
  const setAltPaths = useSetAltPaths();
  useEffect(() => {
    setAltPaths({ dePath, enPath, hasTranslation });
  }, [dePath, enPath, hasTranslation, setAltPaths]);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />

      {hasTranslation && <link rel="alternate" hrefLang="de" href={`${SITE_URL}/de${dePath}`} />}
      {hasTranslation && <link rel="alternate" hrefLang="en" href={`${SITE_URL}/en${enPath}`} />}
      {hasTranslation && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/de${dePath}`} />
      )}

      {(noindex || !hasTranslation) && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
    </Helmet>
  );
}
